import React, { useState, useEffect } from 'react'
import { IconButton, Tooltip, Menu, MenuItem, Typography, Box, Divider, CircularProgress } from '@material-ui/core'
import styled from '@emotion/styled'
import HistoryIcon from '@mui/icons-material/History'
import DeleteIcon from '@mui/icons-material/Delete'
import chatService from '../services/chatService'
import useChat from 'src/components/chatbot/hooks/useChat'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'

const ThreadMenuItem = styled(MenuItem)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1, 2)};
  min-width: 250px;
  max-width: 350px;
  gap: ${({ theme }) => theme.spacing(1)};
`

const ThreadContent = styled(Box)`
  flex: 1;
  min-width: 0; /* This allows the content to shrink */
  overflow: hidden;
`

const ThreadTitle = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`

const DeleteButton = styled(IconButton)`
  padding: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.palette.error.main};
  flex-shrink: 0; /* Prevent the delete button from shrinking */
  min-width: 32px; /* Ensure minimum width for the button */

  &:hover {
    background-color: ${({ theme }) => theme.palette.error.main}20;
  }
`

const LoadingMenuItem = styled(MenuItem)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)};
`

const EmptyMenuItem = styled(MenuItem)`
  padding: ${({ theme }) => theme.spacing(2)};
  text-align: center;
  color: ${({ theme }) => theme.palette.text.secondary};
`

export default function ChatThreadsButton() {
  const [anchorEl, setAnchorEl] = useState(null)
  const [chatThreads, setChatThreads] = useState([])
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [restoring, setRestoring] = useState(false)
  const { restoreChat, clearChatState } = useChat()
  const { chatId } = useChatbotStore()

  const open = Boolean(anchorEl)

  const handleClick = async (event) => {
    setAnchorEl(event.currentTarget)
    await fetchChatThreads()
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const fetchChatThreads = async () => {
    setLoading(true)
    try {
      const threads = await chatService.getChatThreads()
      setChatThreads(threads)
    } catch (error) {
      console.error('Error fetching chat threads:', error)
      setChatThreads([])
    } finally {
      setLoading(false)
    }
  }

  const handleRestoreChat = async (event, threadId) => {
    event.stopPropagation()

    if (restoring === threadId) return

    setRestoring(threadId)
    try {
      const success = await restoreChat(threadId)
      if (success) {
        handleClose()
      }
    } catch (error) {
      console.error('Error restoring chat thread:', error)
    } finally {
      setRestoring(null)
    }
  }

  const handleDeleteThread = async (event, threadId) => {
    event.stopPropagation()

    if (deleting === threadId) return

    setDeleting(threadId)
    try {
      await chatService.deleteChat(threadId)
      setChatThreads((prev) => prev.filter((thread) => thread.id !== threadId))

      if (chatId === threadId) {
        clearChatState()
      }
    } catch (error) {
      console.error('Error deleting chat thread:', error)
    } finally {
      setDeleting(null)
    }
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return 'Unknown'
    }
  }

  return (
    <>
      <Tooltip title="Chat History" placement="bottom">
        <IconButton size="medium" onClick={handleClick}>
          <HistoryIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            maxHeight: 400,
          },
        }}
      >
        {loading ? (
          <LoadingMenuItem disabled>
            <CircularProgress size={20} />
          </LoadingMenuItem>
        ) : chatThreads.length === 0 ? (
          <EmptyMenuItem disabled>No chat threads found</EmptyMenuItem>
        ) : (
          chatThreads.map((thread, index) => (
            <React.Fragment key={thread.id}>
              <ThreadMenuItem onClick={(e) => handleRestoreChat(e, thread.id)} disabled={restoring === thread.id}>
                <ThreadContent>
                  <ThreadTitle variant="body2">{thread.title || 'Untitled Chat'}</ThreadTitle>
                  <Typography variant="caption" color="textSecondary">
                    {formatDate(thread.last_updated)}
                  </Typography>
                </ThreadContent>
                <DeleteButton
                  size="small"
                  onClick={(e) => handleDeleteThread(e, thread.id)}
                  disabled={deleting === thread.id || restoring === thread.id}
                >
                  {deleting === thread.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                </DeleteButton>
              </ThreadMenuItem>
              {index < chatThreads.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
      </Menu>
    </>
  )
}
